/**
 * Mixin for warehouse document tab components (Entry, Invoice, Return, WriteOff).
 * Extracts common patterns: load(), filtered/grouped documents, export, print, excel watchers.
 *
 * Required from the component:
 *   - data: `documentType` (string) — e.g., "entry", "exit", "return", "write-off"
 *   - computed: `headers` (array)
 *   - computed: `exportTitle` (string)
 *
 * Provides:
 *   - data: documents, loading
 *   - methods: load()
 *   - computed: translatedDocuments, filteredDocuments, mobileHeaders, documentsByWarehouse, filters
 *   - watchers: refreshTrigger, isPrinting, isExcelDownloading
 */
import warehouseService from "@/services/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import { isInDateRange, groupByWarehouse } from "@/utils/warehouseUtils";

export default {
    mixins: [hasPrinting, hasWarehousePrinting, hasExcel, hasSnackbar],
    data() {
        return {
            documents: [],
            loading: false,
        };
    },
    computed: {
        lastEntryDate() {
            if (!this.documents.length) return null;
            const sorted = [...this.documents].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            return this.localeDate(sorted[0].created_at, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        filters() {
            return this.$store.state.warehouse.filters;
        },
        mobileHeaders() {
            return this.headers.filter((h) => h.mobile !== false);
        },
        translatedDocuments() {
            return this.documents.map((document) => ({
                ...document,
                number: this.translate(document.number),
                notes: this.translate(document.notes),
            }));
        },
        filteredDocuments() {
            return this.customSearch(
                this.translatedDocuments,
                this.headers,
                this.filters.search
            ).filter((item) => {
                const dateFilter = isInDateRange(
                    item,
                    "document_date",
                    this.filters.dates.from,
                    this.filters.dates.to
                );
                const warehouseFilter =
                    this.filters.warehouse.length === 0 ||
                    this.filters.warehouse.some(
                        (warehouse) => warehouse.id === item.warehouse_id
                    );
                return dateFilter && warehouseFilter;
            });
        },
        documentsByWarehouse() {
            return groupByWarehouse(
                this.filteredDocuments,
                this.$t("stock")
            );
        },
        exportItems() {
            return this.filteredDocuments;
        },
    },
    methods: {
        async load() {
            this.loading = true;
            try {
                const res = await warehouseService.getWarehouseDocuments({
                    document_type: this.documentType,
                });
                this.documents = res.data;
            } catch (e) {
                this.showErrorSnackbar(this.$t("error_loading_documents"));
            } finally {
                this.loading = false;
            }
        },
    },
    watch: {
        "$store.state.warehouse.refreshTrigger"() {
            this.load();
        },
        isExcelDownloading() {
            if (this.isExcelDownloading) {
                this.downloadExcel({
                    fileName: this.exportTitle + ".xlsx",
                    sheets: [
                        {
                            name: this.exportTitle,
                            headers: this.headers,
                            items: this.exportItems,
                            title: this.exportTitle,
                            subtitle: this.subtitle,
                        },
                    ],
                });
            }
        },
        isPrinting(isPrinting) {
            if (isPrinting) {
                this.printDocument({
                    title: this.exportTitle,
                    subtitle: this.subtitle,
                    tables: [
                        {
                            headers: this.headers,
                            items: this.exportItems,
                        },
                    ],
                });
            }
        },
    },
};
